import { Box, Text } from '@chakra-ui/react';
import { useLingui } from '@lingui/react/macro';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useCallback } from 'react';

import { Button, LoadFailed, Loading, NotFoundIcon, Placeholder } from '#features/ui';
import { BlogHeroPost } from '../BlogHeroPost';
import { BlogPostPreview } from '../BlogPostPreview';
import { BlogTagFilter } from '../BlogTagFilter';
import { useBlogPostsPagination } from './useBlogPostsPagination';
import { useBlogTags } from './useBlogTags';

export const BlogPosts = () => {
  const { t } = useLingui();
  const { tag } = useSearch({ from: '/{-$locale}/_layout/blog/' });
  const navigate = useNavigate();
  const tags = useBlogTags();
  const {
    items,
    isLoading,
    isError,
    isFetchNextPageError,
    refetch,
    hasNextPage,
    isFetchingNextPage,
    loadMore,
  } = useBlogPostsPagination(tag);

  const handleTagChange = useCallback(
    (newTag: string | undefined) => {
      navigate({
        to: '/{-$locale}/blog',
        search: newTag ? { tag: newTag } : {},
        replace: true,
      });
    },
    [navigate]
  );

  const heroPost = !tag ? items[0] : undefined;
  const gridPosts = !tag ? items.slice(1) : items;

  return (
    <Box w="100%" maxW="1200px" px={{ base: 4, md: 6 }}>
      <Text mt={{ base: 6, md: 10 }} mb={{ base: 4, md: 6 }} as="h1" textStyle="blog.listHeading">
        {tag || t`blog.latestArticles`}
      </Text>

      <BlogTagFilter tags={tags} activeTag={tag} onTagChange={handleTagChange} />

      {heroPost && <BlogHeroPost {...heroPost} />}

      {gridPosts.length > 0 && (
        <Box
          display="grid"
          gridTemplateColumns={{
            base: '1fr',
            md: 'repeat(2, 1fr)',
            lg: 'repeat(3, 1fr)',
          }}
          gap={5}
        >
          {gridPosts.map((post) => (
            <BlogPostPreview key={post.slug} {...post} />
          ))}
        </Box>
      )}

      {isLoading && <Loading />}
      {isError && !isFetchNextPageError && items.length === 0 && (
        <LoadFailed onRetry={() => void refetch()} retryButtonProps={{ minH: '44px' }} />
      )}
      {isFetchNextPageError ? (
        <Box mt={6}>
          <LoadFailed variant="list" onRetry={loadMore} retryButtonProps={{ minH: '44px' }} />
        </Box>
      ) : hasNextPage ? (
        <Box mt={6} display="flex" justifyContent="center">
          <Button variant="outline" minH="44px" onClick={loadMore} loading={isFetchingNextPage}>
            {t`Show more`}
          </Button>
        </Box>
      ) : null}

      {!isLoading && !isError && items.length === 0 && (
        <Placeholder icon={NotFoundIcon}>
          <Placeholder.Title>{t`posts.finder.placeholder.title`}</Placeholder.Title>
        </Placeholder>
      )}
    </Box>
  );
};

BlogPosts.getLayout = null;
